import { useState } from 'react'
import { Button } from '../../platform/primitives/Button'
import { AddTotp } from './AddTotp'
import { AddPhone } from './AddPhone'
import { syncFactors } from '../../api/mfa'
import './security.css'

type Props = {
  /** Called when the user finishes or declines. Both end the invitation. */
  onSettled: () => void
}

/**
 * The optional first-run invitation to turn on two-step verification.
 *
 * **Optional means genuinely skippable**, and the design carries that rather
 * than claiming it: "Not now" is a real button with equal prominence to the
 * lede, never a greyed link or a hidden corner ✕. A prompt that has to be
 * defeated is a dark pattern, and a security prompt that annoys people is one
 * they learn to dismiss without reading.
 *
 * **Asked once, on the account, not on the device.** The caller decides when to
 * show this from `UserResponse.is_onboarded`, which the backend persists — so
 * declining on a laptop does not re-ask on a phone, and clearing site data does
 * not resurrect it. A `localStorage` flag would have done both.
 *
 * It reuses the same two enrolment components Settings does, so there is one
 * implementation of adding a factor and no second copy to drift.
 *
 * **`<h1>`, not `<h2>`.** This renders under `BareLayout`, which has no
 * `TopBar` — so a bare route owns its own document heading, and this one had
 * none at all. `App.tsx` records that exact regression having been caught once
 * in a browser and missed by the suite; the guard that exists for it iterates a
 * hard-coded list of three pre-login paths and could not see this route, which
 * only renders content for a signed-in user. Covered in `Welcome.test.tsx`,
 * where that state can be arranged.
 */
export function SecurityInvite({ onSettled }: Props) {
  const [adding, setAdding] = useState<'totp' | 'phone' | null>(null)

  if (adding !== null) {
    return (
      <section className="security">
        <h1 className="security__title">
          {adding === 'totp' ? 'Add an authenticator app' : 'Add a phone number'}
        </h1>
        <div className="security__panel">
          {adding === 'totp' ? (
            <AddTotp
              onDone={() => {
                void syncFactors()
                onSettled()
              }}
              onCancel={() => setAdding(null)}
            />
          ) : (
            <AddPhone
              onDone={() => {
                void syncFactors()
                onSettled()
              }}
              onCancel={() => setAdding(null)}
            />
          )}
        </div>
      </section>
    )
  }

  return (
    <section className="security">
      <h1 className="security__title">Add a second step to signing in?</h1>
      <p className="security__lede">
        TrackE5 holds your bank balances and everything you spend. A second code at sign-in means a
        stolen password isn&apos;t enough on its own.
      </p>

      <div className="security__actions">
        <Button variant="primary" size="page" onClick={() => setAdding('totp')}>
          Use an authenticator app
        </Button>
        <Button variant="outline" size="page" onClick={() => setAdding('phone')}>
          Use my phone number
        </Button>
        {/* Same size and shape as the others. See the header: an escape that is
            harder to hit than the thing it escapes is not optional. */}
        <Button variant="outline" size="page" onClick={onSettled}>
          Not now
        </Button>
      </div>

      <p className="security__lede">
        You can turn this on any time from <strong>Settings › Security</strong>.
      </p>
    </section>
  )
}
